import React, { useRef } from 'react';
import { Input } from '@/shared/components/ui/Input';
import { FormField } from '@/shared/components/ui/FormField';

export interface CardFormData {
  holderName: string;
  number: string;
  expiryMonth: string;
  expiryYear: string;
  cvv: string;
  nickname: string;
}

export const emptyCardForm: CardFormData = {
  holderName: '',
  number: '',
  expiryMonth: '',
  expiryYear: '',
  cvv: '',
  nickname: '',
};

interface CardFormFieldsProps {
  card: CardFormData;
  onChange: (card: CardFormData) => void;
  showNickname?: boolean;
}

/**
 * Shared card form fields with auto-advance between inputs.
 * - Card number: 16 digits max, auto-advances to month
 * - Month: 2 digits, auto-advances to year
 * - Year: 2 digits (last two), auto-advances to CVV
 * - CVV: 3 digits
 * All numeric fields accept digits only.
 */
export const CardFormFields: React.FC<CardFormFieldsProps> = ({
  card,
  onChange,
  showNickname = false,
}) => {
  const monthRef = useRef<HTMLInputElement>(null);
  const yearRef = useRef<HTMLInputElement>(null);
  const cvvRef = useRef<HTMLInputElement>(null);

  const digitsOnly = (value: string): string => value.replace(/\D/g, '');

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = digitsOnly(e.target.value).slice(0, 16);
    onChange({ ...card, number: val });
    if (val.length === 16) monthRef.current?.focus();
  };

  const handleMonthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = digitsOnly(e.target.value).slice(0, 2);
    onChange({ ...card, expiryMonth: val });
    if (val.length === 2) yearRef.current?.focus();
  };

  const handleYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = digitsOnly(e.target.value).slice(0, 2);
    onChange({ ...card, expiryYear: val });
    if (val.length === 2) cvvRef.current?.focus();
  };

  const handleCvvChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = digitsOnly(e.target.value).slice(0, 3);
    onChange({ ...card, cvv: val });
  };

  return (
    <div className="space-y-3">
      <FormField label="Nome do titular" required>
        <Input
          value={card.holderName}
          onChange={(e) => onChange({ ...card, holderName: e.target.value })}
          placeholder="Nome como no cartao"
        />
      </FormField>

      <FormField label="Número do cartão" required>
        <Input
          value={card.number}
          onChange={handleNumberChange}
          placeholder="0000 0000 0000 0000"
          maxLength={16}
          inputMode="numeric"
        />
      </FormField>

      <div className="grid grid-cols-3 gap-3">
        <FormField label="Mes" required>
          <Input
            ref={monthRef}
            value={card.expiryMonth}
            onChange={handleMonthChange}
            placeholder="MM"
            maxLength={2}
            inputMode="numeric"
          />
        </FormField>
        <FormField label="Ano" required>
          <Input
            ref={yearRef}
            value={card.expiryYear}
            onChange={handleYearChange}
            placeholder="AA"
            maxLength={2}
            inputMode="numeric"
          />
        </FormField>
        <FormField label="CVV" required>
          <Input
            ref={cvvRef}
            value={card.cvv}
            onChange={handleCvvChange}
            placeholder="123"
            maxLength={3}
            inputMode="numeric"
          />
        </FormField>
      </div>

      {showNickname && (
        <FormField label="Apelido" description="Opcional">
          <Input
            value={card.nickname}
            onChange={(e) => onChange({ ...card, nickname: e.target.value })}
            placeholder="Ex: Cartao pessoal"
          />
        </FormField>
      )}
    </div>
  );
};
